import React, { useMemo, useState } from 'react';
import { Form, Formik, FormikHelpers, Field, FieldProps, ErrorMessage, FormikProps } from 'formik';
import Button from '../../../../ui/Buttons/Button/Button';
import FormField from '../../../../ui/DataEntry/FormField/FormField';
import Label from '../../../../ui/DataEntry/Label/Label';
import FileUpload from '../../../../ui/DataEntry/FileUpload/FileUpload';
import FileUploadImagePreview from '../../../../ui/DataEntry/FileUpload/FileUploadImagePreview/FileUploadImagePreview';
import { usePaginatedFetch, getArtists, getSpotifySongs } from '@round/api';
import { GenericDropdownOption } from '../../../../App.types';
import Select from '../../../../ui/DataEntry/Select/Select';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';
import { StylesConfig } from 'react-select';
import styles from './SongForm.module.css';
import { SongApiModel } from '@round/api';
import { DropdownOptionWithSubLabel, OptionWithSubLabel } from '@round/ui-kit';

const selectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#c2cfe0',
    }),
    valueContainer: (base, state) => ({
        ...base,
        fontSize: '0.8125rem',
        padding: state.isMulti ? '0.625rem 1rem 0.4375rem 1rem' : '0.625rem 1rem',
    }),
    multiValue: (base) => ({
        ...base,
        margin: '0 3px 3px 0',
    }),
    multiValueRemove: (base) => ({
        ...base,
        height: '1rem',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#90a0b7',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: '#90a0b7',
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 1000,
    }),
};

type SongFormValues = Pick<SongApiModel, 'title' | 'soundcloud_url'> & {
    image: File | null;
    artists: GenericDropdownOption<number>[];
    spotify_song: GenericDropdownOption<number> | null;
};

export type SongFormProps = {
    handleSubmit: (values: SongFormValues, helpers: FormikHelpers<SongFormValues>) => void;
    initialValues: SongFormValues;
    renderActions?: (props: FormikProps<SongFormValues>) => React.ReactNode;
};

const defaultRenderActions = ({ isSubmitting, submitForm }: FormikProps<SongFormValues>) => (
    <Button className={styles.submitButton} type="filled" color="black" loading={isSubmitting} onClick={submitForm}>
        Save
    </Button>
);

const SongForm = ({ handleSubmit, initialValues, renderActions = defaultRenderActions }: SongFormProps) => {
    const [artistSearch, setArtistSearch] = useState('');
    const [songSearch, setSongSearch] = useState('');
    const [page, setPage] = useState(1);
    const {
        data: artistData,
        loading: artistsLoading,
        getCurrentPage: getCurrentArtistPage,
        hasNextPage: hasNextArtistPage,
    } = usePaginatedFetch(getArtists, {
        search: artistSearch,
        page,
        page_size: 10,
    });
    const {
        data: songData,
        loading: songsLoading,
        getCurrentPage: getCurrentSongPage,
        hasNextPage: hasNextSongPage,
    } = usePaginatedFetch(getSpotifySongs, {
        search: songSearch,
        page,
        page_size: 10,
    });

    const artistOptions: GenericDropdownOption<number>[] = useMemo(
        () =>
            artistData.map((a) => ({
                value: a.id,
                label: a.name,
            })),
        [artistData]
    );

    const songOptions: DropdownOptionWithSubLabel<number>[] = useMemo(
        () =>
            songData.map((a) => ({
                value: a.id,
                label: a.title,
                subLabel: a.artists.map((a) => a.name).join(', '),
            })),
        [songData]
    );

    useAbortableEffect(
        (signal) => {
            getCurrentArtistPage({ signal });
        },
        [getCurrentArtistPage]
    );

    useAbortableEffect(
        (signal) => {
            getCurrentSongPage({ signal });
        },
        [getCurrentSongPage]
    );

    return (
        <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
            {(props) => (
                <>
                    <Form className={styles.form}>
                        <FormField>
                            <Label htmlFor="title">Song Name</Label>
                            <Field name="title" id="title" placeholder="Enter song name" />
                            <ErrorMessage name="title">
                                {(msg) => <span className={styles.error}>{msg}</span>}
                            </ErrorMessage>
                        </FormField>

                        <FormField>
                            <Label htmlFor="Spotify Song">Spotify Song</Label>
                            <Field name="spotify_song">
                                {(props: FieldProps) => (
                                    <Select
                                        isClearable
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        isLoading={songsLoading}
                                        inputValue={songSearch}
                                        onInputChange={setSongSearch}
                                        options={songOptions}
                                        value={props.field.value}
                                        onChange={(value) => props.form.setFieldValue(props.field.name, value)}
                                        onMenuScrollToBottom={() => {
                                            if (hasNextSongPage && !songsLoading) {
                                                setPage((page) => page + 1);
                                            }
                                        }}
                                        components={{ Option: OptionWithSubLabel }}
                                    />
                                )}
                            </Field>
                            <ErrorMessage name="spotify_song">
                                {(msg) => <span className={styles.error}>{msg}</span>}
                            </ErrorMessage>
                        </FormField>

                        <FormField>
                            <Label htmlFor="artists">Artists</Label>
                            <Field name="artists">
                                {(props: FieldProps) => (
                                    <Select
                                        isMulti
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        isLoading={artistsLoading}
                                        inputValue={artistSearch}
                                        onInputChange={setArtistSearch}
                                        options={artistOptions}
                                        value={props.field.value}
                                        onChange={(value) => props.form.setFieldValue(props.field.name, value)}
                                        onMenuScrollToBottom={() => {
                                            if (hasNextArtistPage && !artistsLoading) {
                                                setPage((page) => page + 1);
                                            }
                                        }}
                                    />
                                )}
                            </Field>
                            <ErrorMessage name="artists">
                                {(msg) => <span className={styles.error}>{msg}</span>}
                            </ErrorMessage>
                        </FormField>

                        <FormField>
                            <Label htmlFor="soundcloud_url">Soundcloud URL</Label>
                            <Field name="soundcloud_url" id="soundcloud_url" placeholder="Enter soundcloud url" />
                            <ErrorMessage name="soundcloud_url">
                                {(msg) => <span className={styles.error}>{msg}</span>}
                            </ErrorMessage>
                        </FormField>

                        <FormField>
                            <Label htmlFor="image">Image</Label>
                            <Field name="image" id="image">
                                {(props: FieldProps) => (
                                    <FileUpload
                                        files={[props.field.value]}
                                        onUpload={([image]) => props.form.setFieldValue('image', image ?? null)}
                                    >
                                        {({ files: [image] }) => (
                                            <FileUploadImagePreview className={styles.imagePreview} file={image} />
                                        )}
                                    </FileUpload>
                                )}
                            </Field>
                            <ErrorMessage name="image">
                                {(msg) => <span className={styles.error}>{msg}</span>}
                            </ErrorMessage>
                        </FormField>
                    </Form>
                    {renderActions(props)}
                </>
            )}
        </Formik>
    );
};
export default SongForm;
